import { defineStore } from 'pinia';
import { setSandboxActive } from '@/lib/session';
import { relancerTempsReel } from '@/lib/realtime';
import { TOUR_CHAPTERS, chapterById } from '@/components/tour/tourSteps';

/**
 * Mode découverte : visite guidée interactive sur un bac à sable isolé par
 * utilisateur. Le store pilote (a) le pont X-Sandbox (via lib/session) pour que
 * toutes les requêtes tapent dans l'espace de test de l'utilisateur, et (b)
 * l'état du moteur de visite (chapitre / étape courants, progression).
 *
 * Persistance : localStorage `pa.discovery.${userId}` (convention `pa.` existante).
 * On y garde le mode actif et la progression, de sorte qu'un rechargement en
 * pleine découverte reste dans le bac à sable et retrouve les chapitres terminés.
 */

interface Persisted {
  active: boolean;
  completed: string[];
}

interface State {
  /** Mode découverte actif (le pont X-Sandbox suit). */
  active: boolean;
  /** Chapitre en cours de visite, ou null quand on est au menu. */
  chapterId: string | null;
  /** Étape courante dans le chapitre. */
  stepIndex: number;
  /** Ids des chapitres terminés (progression). */
  completed: string[];
  /** Utilisateur propriétaire de la progression persistée. */
  userId: string | null;
}

function storageKey(userId: string): string {
  return `pa.discovery.${userId}`;
}

export const useDiscoveryStore = defineStore('discovery', {
  state: (): State => ({
    active: false,
    chapterId: null,
    stepIndex: 0,
    completed: [],
    userId: null,
  }),
  getters: {
    /** Chapitre courant résolu (déclaratif). */
    currentChapter: (s) => (s.chapterId ? chapterById(s.chapterId) : null),
    /** Étape courante résolue. */
    currentStep(s): (typeof TOUR_CHAPTERS)[number]['steps'][number] | null {
      const ch = s.chapterId ? chapterById(s.chapterId) : null;
      return ch ? (ch.steps[s.stepIndex] ?? null) : null;
    },
    /** Une visite de chapitre est en cours (une bulle doit s'afficher). */
    inTour: (s) => s.active && s.chapterId !== null,
    isCompleted: (s) => (id: string) => s.completed.includes(id),
    /** Progression globale, en pourcentage de chapitres terminés. */
    progressPct: (s) =>
      TOUR_CHAPTERS.length === 0
        ? 0
        : Math.round((s.completed.length / TOUR_CHAPTERS.length) * 100),
  },
  actions: {
    /**
     * Restaure l'état persisté pour l'utilisateur et rebranche le pont si le
     * mode était actif (rechargement en pleine découverte). À appeler une fois
     * l'identité connue.
     */
    hydrate(userId: string): void {
      this.userId = userId;
      try {
        const raw = localStorage.getItem(storageKey(userId));
        if (raw) {
          const data = JSON.parse(raw) as Partial<Persisted>;
          this.active = data.active === true;
          this.completed = Array.isArray(data.completed) ? data.completed : [];
        }
      } catch {
        // localStorage indisponible ou corrompu : on repart d'un état vierge.
      }
      // Rebranche le pont selon l'état restauré (au menu, sans chapitre ouvert).
      setSandboxActive(this.active);
    },

    persist(): void {
      if (!this.userId) return;
      const data: Persisted = { active: this.active, completed: this.completed };
      try {
        localStorage.setItem(storageKey(this.userId), JSON.stringify(data));
      } catch {
        // Écriture best-effort : la progression n'est pas critique.
      }
    },

    /** Entre en mode découverte : le pont X-Sandbox s'active. */
    start(): void {
      this.active = true;
      setSandboxActive(true);
      // Le flux temps réel porte le contexte dans ses en-têtes : sans le
      // rouvrir, on resterait abonné aux changements de la VRAIE donnée tout en
      // regardant le bac à sable.
      relancerTempsReel();
      this.persist();
    },

    /** Sort du mode découverte : retour aux vraies données. */
    stop(): void {
      this.active = false;
      this.chapterId = null;
      this.stepIndex = 0;
      setSandboxActive(false);
      relancerTempsReel();
      this.persist();
    },

    /** Démarre (ou reprend) la visite d'un chapitre. */
    startChapter(id: string): void {
      if (!chapterById(id)) return;
      if (!this.active) this.start();
      this.chapterId = id;
      this.stepIndex = 0;
    },

    /** Ferme la bulle sans quitter le mode découverte (retour au menu). */
    closeTour(): void {
      this.chapterId = null;
      this.stepIndex = 0;
    },

    next(): void {
      const ch = this.currentChapter;
      if (!ch) return;
      if (this.stepIndex >= ch.steps.length - 1) {
        this.completeChapter(ch.id);
        this.closeTour();
      } else {
        this.stepIndex += 1;
      }
    },

    prev(): void {
      if (this.stepIndex > 0) this.stepIndex -= 1;
    },

    completeChapter(id: string): void {
      if (!this.completed.includes(id)) {
        this.completed.push(id);
        this.persist();
      }
    },

    /** Réinitialise la progression (n'affecte pas les données du bac à sable). */
    resetProgress(): void {
      this.completed = [];
      this.persist();
    },
  },
});
